import {
  appsScriptHtmlErrorMessage,
  APPS_SCRIPT_ACCESS_MESSAGE,
  isGoogleAuthHtml,
  isGoogleDriveHtmlError,
} from "../utils/appsScriptResponse";

const PROXY_PATH = "/api/submit-order";

function apiNotFoundMessage(data) {
  const bodyHint = typeof data?.raw === "string" ? data.raw : "";
  const onDeadVercel =
    bodyHint.includes("DEPLOYMENT_NOT_FOUND") ||
    bodyHint.includes("deployment could not be found");
  if (onDeadVercel) {
    return (
      "Site DNS still points to Vercel (no deployment). Connect gift_box_react on Netlify, " +
      "deploy, point rencindesign.com DNS to Netlify, then set REACT_APP_ORDER_SUBMIT_URL."
    );
  }
  return (
    "Login API not found (404). Deploy on Netlify with netlify/functions, set " +
    "REACT_APP_ORDER_SUBMIT_URL to your Apps Script /exec URL, and redeploy."
  );
}

function parseJsonResponse(text, status) {
  if (!text) return null;
  if (status === 401 || status === 403 || isGoogleAuthHtml(text)) {
    return { ok: false, message: APPS_SCRIPT_ACCESS_MESSAGE };
  }
  if (isGoogleDriveHtmlError(text)) {
    return { ok: false, message: appsScriptHtmlErrorMessage() };
  }
  try {
    return JSON.parse(text);
  } catch {
    if (String(text).trim().startsWith("<")) {
      return { ok: false, message: appsScriptHtmlErrorMessage() };
    }
    return { raw: text };
  }
}

async function postOrder(url, body, contentType) {
  const res = await fetch(url, {
    method: "POST",
    body,
    headers: {
      "Content-Type": contentType,
    },
  });
  const text = await res.text();
  const data = parseJsonResponse(text, res.status);
  return { res, text, data };
}

/**
 * POST order JSON via /api/submit-order (Netlify Function or CRA dev proxy → Apps Script).
 */
export async function submitOrderToSheet(payload) {
  const body = JSON.stringify(payload);

  try {
    const { res, data } = await postOrder(
      PROXY_PATH,
      body,
      "application/json;charset=utf-8",
    );

    if (!res.ok) {
      if (res.status === 404) {
        return { ok: false, message: apiNotFoundMessage(data), data };
      }
      if (res.status === 503 && data?.message) {
        return { ok: false, message: data.message, data };
      }
      if (res.status === 401) {
        return {
          ok: false,
          message:
            data?.message ||
            "Google Apps Script returned 401 (not allowed). In Apps Script → Deploy → Web app, set **Who has access** to **Anyone**, then deploy a new version.",
          data,
        };
      }
      return {
        ok: false,
        message: data?.message || `Server error (${res.status})`,
        data,
      };
    }

    if (data && data.ok === false) {
      return {
        ok: false,
        message: data.message || "Order could not be saved.",
        data,
      };
    }

    return { ok: true, data, warning: data?.warning };
  } catch (e) {
    return {
      ok: false,
      message: e?.message || "Network error while saving the order.",
    };
  }
}

/** POST portal actions — same URL as order submit. Uses form body on /api for reliable parsing. */
export async function postOrderPortal(payload) {
  const json = JSON.stringify(payload);
  const params = new URLSearchParams();
  params.set("json", json);

  try {
    const { res, data } = await postOrder(
      PROXY_PATH,
      params.toString(),
      "application/x-www-form-urlencoded;charset=UTF-8",
    );

    if (!res.ok) {
      if (res.status === 404) {
        return { ok: false, message: apiNotFoundMessage(data), data };
      }
      if (res.status === 401) {
        return {
          ok: false,
          message: data?.message || APPS_SCRIPT_ACCESS_MESSAGE,
          data,
        };
      }
      if (data?.message) {
        return { ok: false, message: data.message, data };
      }
      return {
        ok: false,
        message: data?.message || `Server error (${res.status})`,
        data,
      };
    }
    if (data && data.ok === false) {
      return {
        ok: false,
        message: data.message || "Request failed.",
        data,
      };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, message: e?.message || "Network error." };
  }
}
